import { forwardRef } from "react";

import { Box, SxProps, Theme, useTheme } from "@mui/material";
import ButtonBase, { ButtonBaseProps } from "@mui/material/ButtonBase";

import { mergeSx } from "@/common/helpers/mergeSx";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { Shop } from "@/icons/outlined";

interface FormCartWidgetProps extends Omit<ButtonBaseProps, "children"> {
  count?: number;
  showCounter?: boolean;
  sx?: SxProps<Theme>;
}

export const FormCartWidget = forwardRef<HTMLButtonElement, FormCartWidgetProps>(
  ({ count = 0, showCounter = false, sx, ...rest }, ref) => {
    const theme = useTheme();
    const { t } = useTranslate();

    if (!theme.palette.forms || !theme.palette.formsComponents?.global || !theme.palette.formsComponents?.cartWidget) {
      return <InvalidThemeErrorMessage />;
    }

    const tokens = theme.palette.formsComponents.cartWidget;

    const containerStyles: SxProps<Theme> = mergeSx(
      {
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 48,
        height: 48,
        borderRadius: theme.radius(1),
        backgroundColor: tokens.backgroundColor,
        border: tokens.border,
        boxShadow: theme.boxShadow.base.light,
        color: tokens.iconColor,
        cursor: "pointer",
        transition: "background-color 200ms ease-out, outline 100ms ease-out",
        outlineColor: "transparent",
        "&:hover": {
          backgroundColor: tokens.hoverBackgroundColor,
        },
        "&:focus-visible": {
          outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
        },
      },
      sx
    );

    return (
      <ButtonBase
        ref={ref}
        disableRipple
        aria-label={t("common", "a11y.cart", { count })}
        sx={containerStyles}
        {...rest}
      >
        <FormIcon size="large">
          <Shop />
        </FormIcon>
        {showCounter && (
          <Box
            sx={{
              position: "absolute",
              top: -6,
              left: -7,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 20,
              height: 20,
              borderRadius: "100px",
              backgroundColor: tokens.badgeBackgroundColor,
              color: tokens.badgeTextColor,
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "20px",
              letterSpacing: "0.15px",
            }}
          >
            {count}
          </Box>
        )}
      </ButtonBase>
    );
  }
);

FormCartWidget.displayName = "FormCartWidget";
